import React, { useCallback, useEffect, useState } from 'react'
import { Moment } from '../../Libs/moment'

import { DateTimePicker, DateTimePickerProps as MiuDateRangePickerProps } from '@mui/x-date-pickers'
import { IconButton, TextField, Stack } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { B4Calendar, J1LeftarrowD, J1RightarrowD } from '@e3dc-react/icons'
import { useDatePickerValidation } from '../../Hooks/useValidation'

const useStyles = makeStyles()(theme => ({
  input: {
    margin: ` ${theme.spacing(1)}`,
  },
  pointer: {
    cursor: 'pointer',
    marginRight: theme.spacing(1),
  },
}))

export interface DateRangePickerProps extends Partial<MiuDateRangePickerProps<Moment, Moment>> {
  handleFromChange: (date: Moment | null) => void
  handleToChange: (date: Moment | null) => void
  fromMinDate?: Moment
  fromMinDateMessage?: string
  fromMaxDate?: Moment
  fromMaxDateMessage?: string
  toMinDate?: Moment
  toMinDateMessage?: string
  toMaxDate?: Moment
  toMaxDateMessage?: string
  fromLabel: string
  toLabel: string
  valueFrom: Moment
  valueTo: Moment
  onNextClick: () => void
  onPrevClick: () => void
  dataTagPrefix?: string
  withoutNextPreviousButtons?: boolean
  errorDateFormat?: string
}
const DateRangePicker: React.VFC<DateRangePickerProps> = ({
  handleFromChange,
  handleToChange,
  fromMinDate,
  fromMinDateMessage,
  fromMaxDate,
  fromMaxDateMessage,
  toMinDate,
  toMinDateMessage,
  toMaxDate,
  toMaxDateMessage,
  fromLabel,
  toLabel,
  valueFrom,
  valueTo,
  onNextClick,
  onPrevClick,
  dataTagPrefix,
  withoutNextPreviousButtons = false,
  errorDateFormat,
  ...otherProps
}) => {
  const [selectedFrom, setSelectedFrom] = useState<Moment>(valueFrom)
  const [selectedTo, setSelectedTo] = useState<Moment>(valueTo)
  const { classes } = useStyles()
  const reachedMax = selectedTo.isSame(toMaxDate) || selectedFrom.isSame(fromMaxDate)
  const reachedMin = selectedFrom.isSame(fromMinDate) || selectedTo.isSame(toMinDate)
  const [openFrom, setOpenFrom] = useState(false)
  const [openTo, setOpenTo] = useState(false)
  const [fromError, setFromError] = useState<boolean>(false)
  const [fromErrorMessage, setFromErrorMessage] = useState<string>()
  const [toError, setToError] = useState<boolean>(false)
  const [toErrorMessage, setToErrorMessage] = useState<string>()

  useEffect(() => {
    setSelectedFrom(valueFrom)
  }, [valueFrom])

  useEffect(() => {
    setSelectedTo(valueTo)
  }, [valueTo])

  const { validateDate } = useDatePickerValidation()

  /* handle from date picker change including error handling */
  const onFromChange = useCallback(
    (date: Moment | null): void => {
      if (date === null) return

      const { error, errorMessage } = validateDate(date, fromMinDate, fromMaxDate, errorDateFormat)

      if (error) {
        setFromErrorMessage(errorMessage)
        setFromError(error)
        return
      }

      setFromErrorMessage(undefined)
      setFromError(false)

      setSelectedFrom(date)

      if (handleFromChange && !selectedFrom.isSame(date)) {
        handleFromChange(date)
      }
    },
    [validateDate, fromMinDate, fromMaxDate, errorDateFormat, handleFromChange, selectedFrom]
  )

  /* handle to date picker change including error handling */
  const onToChange = useCallback(
    (date: Moment | null): void => {
      if (date === null) return
      const { error, errorMessage } = validateDate(date, toMinDate, toMaxDate, errorDateFormat)

      if (error) {
        setToErrorMessage(errorMessage)
        setToError(error)
        return
      }

      setToErrorMessage(undefined)
      setToError(false)
      /* range view to value is inclusive - from 01.01.2020 to 01.01.2020 means 24 hours on that day */
      const newDate = date.clone().endOf('day')

      setSelectedTo(newDate)

      if (handleToChange && !selectedTo.isSame(newDate)) {
        handleToChange(newDate)
      }
    },
    [validateDate, toMinDate, toMaxDate, errorDateFormat, handleToChange, selectedTo]
  )

  /**
   * select following date on right arrow click
   */
  const handleNextClick = (): void => {
    const nextDateFrom = selectedFrom?.clone() ?? null
    nextDateFrom?.add(1, 'days')
    const nextDateTo = selectedTo?.clone() ?? null
    nextDateTo?.add(1, 'days')

    setSelectedFrom(nextDateFrom)
    setSelectedTo(nextDateTo)
    handleToChange(nextDateTo)
    handleFromChange(nextDateFrom)
    if (onNextClick) {
      onNextClick()
    }
  }

  /**
   * select previous date on left arrow click
   */
  const handlePrevClick = (): void => {
    const nextDateFrom = selectedFrom?.clone() ?? null
    nextDateFrom?.subtract(1, 'days')
    const nextDateTo = selectedTo?.clone() ?? null
    nextDateTo?.subtract(1, 'days')
    setSelectedFrom(nextDateFrom)
    setSelectedTo(nextDateTo)
    handleToChange(nextDateTo)
    handleFromChange(nextDateFrom)
    if (onPrevClick) {
      onPrevClick()
    }
  }

  return (
    <Stack spacing={2} direction="row">
      {!withoutNextPreviousButtons && (
        <IconButton
          color="primary"
          onClick={handlePrevClick}
          disabled={reachedMin}
          data-qa={`button-${dataTagPrefix}-date-range-picker-prev`}
          size="large"
        >
          <J1LeftarrowD fontSize="large" />
        </IconButton>
      )}
      <DateTimePicker
        {...otherProps}
        open={openFrom}
        onClose={() => setOpenFrom(false)}
        renderInput={props => (
          <TextField
            {...props}
            error={fromError}
            helperText={fromErrorMessage}
            variant={'outlined'}
            className={classes.pointer}
            InputProps={{
              endAdornment: (
                <B4Calendar
                  className={classes.pointer}
                  onClick={() => {
                    setOpenTo(false)
                    setOpenFrom(true)
                  }}
                />
              ),
            }}
            inputProps={{
              value: selectedFrom?.format(otherProps.inputFormat) || '',
            }}
          />
        )}
        value={selectedFrom}
        onChange={onFromChange}
        className={classes.input}
        minDate={fromMinDate}
        maxDate={fromMaxDate}
        label={fromLabel}
        data-qa={`${dataTagPrefix}-date-range-picker-from`}
      />
      <DateTimePicker
        {...otherProps}
        open={openTo}
        onClose={() => setOpenTo(false)}
        renderInput={props => {
          return (
            <TextField
              {...props}
              error={toError}
              helperText={toErrorMessage}
              variant={'outlined'}
              className={classes.pointer}
              InputProps={{
                endAdornment: (
                  <B4Calendar
                    className={classes.pointer}
                    onClick={() => {
                      setOpenFrom(false)
                      setOpenTo(true)
                    }}
                  />
                ),
              }}
              inputProps={{
                value: selectedTo?.format(otherProps.inputFormat) || '',
              }}
            />
          )
        }}
        value={selectedTo}
        onChange={onToChange}
        className={classes.input}
        minDate={toMinDate}
        maxDate={toMaxDate}
        label={toLabel}
        data-qa={`${dataTagPrefix}-date-range-picker-to`}
      />
      {!withoutNextPreviousButtons && (
        <IconButton
          color="primary"
          onClick={handleNextClick}
          disabled={reachedMax}
          data-qa={`button-${dataTagPrefix}-date-range-picker-next`}
          size="large"
        >
          <J1RightarrowD fontSize="large" />
        </IconButton>
      )}
    </Stack>
  )
}

export default DateRangePicker
